import { memo, useCallback, useMemo } from 'react';

import { useTrackOpenIntercomChat } from '@air/analytics';
import { AlertBanner } from '@air/primitive-alert-banner';
import { useAirModal } from '@air/provider-modal';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useStorageLimitBannerDismiss } from '~/components/WorkspaceStorageStatusBanner/hooks/useStorageLimitBannerDismiss';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const WorkspaceStorageStatusBanner = memo(() => {
  const { data: subscription } = useSubscriptionContext();
  const { members } = useWorkspaceMembers();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: storage } = useStorage();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const [showPlansModal] = useAirModal(PlansModal);
  const [showDemoFormModal] = useAirModal(DemoFormModal);
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();

  const canEditPlan = canManageSubscription(workspacePermissions);
  const isOutOfStorage = storage?.isOutOfStorage;
  const isAlmostOutOfStorage = storage?.isAlmostOutOfStorage;

  const { isDismissed, dismiss } = useStorageLimitBannerDismiss({
    canEditPlan,
    isOutOfStorage: !!isOutOfStorage,
  });

  const handleUpgrade = useCallback(() => {
    showPlansModal();
  }, [showPlansModal]);

  const handleOpenDemoForm = useCallback(() => {
    showDemoFormModal();
  }, [showDemoFormModal]);

  const message = useMemo(() => {
    if (isOutOfStorage) {
      if (canEditPlan) {
        return 'Your workspace has reached its storage limit. Uploads are paused until you upgrade.';
      }

      return 'This workspace has reached its storage limit. Uploads are paused until your admin upgrades the plan.';
    }
    if (canEditPlan) {
      return "You're approaching your storage limit. Upgrade your plan to keep uploading without interruption.";
    }

    return 'Your workspace is approaching its storage limit. Notify your admin to upgrade and avoid upload interruptions.';
  }, [isOutOfStorage, canEditPlan]);

  const action = useMemo(() => {
    if (!canEditPlan) {
      return null;
    }

    if (subscription?.isOnEnterprisePlan) {
      return (
        <button className="font-semibold text-inherit" onClick={trackOpenIntercomChat} type="button">
          Talk to support →
        </button>
      );
    }

    if (subscription?.isOnBusinessPlan) {
      return (
        <button
          className="font-semibold text-inherit"
          data-testid="STORAGE_BANNER_CONTACT_SUPPORT_BUTTON"
          onClick={handleOpenDemoForm}
          type="button"
        >
          Contact support →
        </button>
      );
    }

    return (
      <button className="font-semibold text-inherit" onClick={handleUpgrade} type="button">
        Upgrade now →
      </button>
    );
  }, [
    canEditPlan,
    subscription?.isOnEnterprisePlan,
    subscription?.isOnBusinessPlan,
    trackOpenIntercomChat,
    handleUpgrade,
    handleOpenDemoForm,
  ]);

  if (!currentWorkspace || !storage || !members || (!isOutOfStorage && !isAlmostOutOfStorage) || isDismissed) {
    return null;
  }

  return (
    <AlertBanner
      color={isOutOfStorage ? 'red' : 'yellow'}
      data-testid="OUT_OF_STORAGE_BANNER"
      onDismiss={isOutOfStorage ? undefined : dismiss}
    >
      {message} {action}
    </AlertBanner>
  );
});

WorkspaceStorageStatusBanner.displayName = 'WorkspaceStorageStatusBanner';
